// plyIntegrity — self-describing truncation check for binary PLY splat files.
//
// A binary PLY's ASCII header declares exactly how many bytes the body must
// hold (element counts × fixed property sizes), so a truncated download is
// detectable with no server metadata at all — the same idea as glbIntegrity.js
// for GLBs. This matters because the environment file route streams with
// Transfer-Encoding: chunked and no Content-Length, so a stream an intermediary
// cut CLEANLY looks complete to fetch(): the only witness left is the header's
// own declaration. (Observed: a cached 126 MB copy of a 568 MB Startup_Level.ply
// hung Spark's decode worker at "Expected 2406393 splats, got 535587".)
//
// ONE-SIDED, like every integrity check in this repo (glbIntegrity, cacheFirst):
// only a body SHORTER than declared fails — truncation cannot add bytes, and
// trailing padding is a server quirk, not corruption.
//
// Verifiability is honest: anything this module can't fully account for — an
// ASCII PLY, a list property (variable length), an unknown property type, a
// non-PLY format (spz/splat/ksplat) — returns null, meaning "cannot verify",
// never a guess in either direction.

// Fixed-size PLY property types (bytes). List properties are variable-length
// and make the size undecidable from the header alone.
const TYPE_SIZES = {
  char: 1, int8: 1, uchar: 1, uint8: 1,
  short: 2, int16: 2, ushort: 2, uint16: 2,
  int: 4, int32: 4, uint: 4, uint32: 4, float: 4, float32: 4,
  double: 8, float64: 8,
}

// How much of the file we need to see to find `end_header`. INRIA splat PLYs
// have ~60 property lines (~1.5 KB); 16 KB is generous headroom for comments.
export const PLY_HEADER_SCAN_BYTES = 16384

/**
 * Parse a binary-little/big-endian PLY header from the first bytes of a file.
 *
 * @param {Uint8Array} bytes — at least the first PLY_HEADER_SCAN_BYTES of the
 *   file (fewer is fine if the header fits).
 * @returns {{ vertexCount: number, headerLength: number, bodyLength: number,
 *   expectedByteLength: number } | null} — null when the size cannot be
 *   determined (not a binary PLY, list properties, unknown types, header not
 *   fully inside `bytes`).
 */
export function parsePlyHeader(bytes) {
  if (!bytes || bytes.length < 4) return null
  // Magic: 'ply\n' (allow \r\n after 'ply' too).
  if (bytes[0] !== 0x70 || bytes[1] !== 0x6c || bytes[2] !== 0x79) return null

  // Find 'end_header\n' within the scan window. Decode ASCII only up to there.
  const text = asciiDecode(bytes)
  const endMatch = /end_header\r?\n/.exec(text)
  if (!endMatch) return null // header not in view → cannot verify
  const headerLength = endMatch.index + endMatch[0].length
  const header = text.slice(0, endMatch.index)

  const lines = header.split(/\r?\n/)
  let binary = false
  let bodyLength = 0
  let vertexCount = 0
  let currentCount = null // element row count while walking its properties
  let currentRowSize = 0
  let sawVertex = false

  const flush = () => {
    if (currentCount != null) bodyLength += currentCount * currentRowSize
  }

  for (const line of lines) {
    const parts = line.trim().split(/\s+/)
    if (parts[0] === 'format') {
      if (parts[1] !== 'binary_little_endian' && parts[1] !== 'binary_big_endian') return null // ascii → size undecidable
      binary = true
    } else if (parts[0] === 'element') {
      flush()
      currentCount = Number(parts[2])
      currentRowSize = 0
      if (!Number.isFinite(currentCount) || currentCount < 0) return null
      if (parts[1] === 'vertex') { vertexCount = currentCount; sawVertex = true }
    } else if (parts[0] === 'property') {
      if (currentCount == null) return null // property before any element — malformed
      if (parts[1] === 'list') return null   // variable-length → cannot verify
      const size = TYPE_SIZES[parts[1]]
      if (!size) return null                 // unknown type → cannot verify
      currentRowSize += size
    }
  }
  flush()

  if (!binary || !sawVertex || bodyLength <= 0) return null
  return { vertexCount, headerLength, bodyLength, expectedByteLength: headerLength + bodyLength }
}

/**
 * Why a PLY of `actualByteLength` bytes is unusable given its own header, or
 * null when it's fine OR cannot be verified. One-sided: only short fails.
 *
 * @param {Uint8Array} headerBytes — first bytes of the file (a slice is fine).
 * @param {number} actualByteLength — the full file/blob size.
 */
export function plyTruncationReason(headerBytes, actualByteLength) {
  const h = parsePlyHeader(headerBytes)
  if (!h) return null // not verifiable — never reject what we can't account for
  if (actualByteLength < h.expectedByteLength) {
    const pct = ((actualByteLength / h.expectedByteLength) * 100).toFixed(1)
    return `truncated PLY: header declares ${h.expectedByteLength} bytes ` +
      `(${h.vertexCount} splats) but the file holds ${actualByteLength} (${pct}%)`
  }
  return null
}

// ASCII-decode without TextDecoder so the module stays importable under
// node --test with zero setup (TextDecoder exists in node, but a manual loop
// over ≤16 KB is trivially cheap and dependency-free).
function asciiDecode(bytes) {
  const n = Math.min(bytes.length, PLY_HEADER_SCAN_BYTES)
  let s = ''
  for (let i = 0; i < n; i++) s += String.fromCharCode(bytes[i])
  return s
}
